/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {PrimaryButton} from '../../../components/buttons/current/PrimaryButton';
import {TertiaryButton} from '../../../components/buttons/current/TertiaryButton';
import TextField from '../../../components/textfield/current/TextField';
import {dlsColor} from '../../../theme/theme';
import {SavedMapViewType} from '../../../types/types';
import {useGeofenceCardStyles} from '../../geofences/geofenceCardStyles';
import {useSavedMapViewName} from './useSavedMapViewName';

export type SavedMapViewsCurrentInputProps = {
  savedMapViews?: SavedMapViewType[];
  mapViewToEdit?: SavedMapViewType;
  onCancel?: Function;
  onSave?: Function;
};

const useStyles = makeStyles(() => ({
  body : {
    paddingTop : 8
  },
  // the saved parameters read as the card's normal body text ...
  parameters : {
    margin     : 0,
    fontFamily : 'Inter',
    fontWeight : 400,
    fontSize   : '14px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    '& ul'     : {
      margin      : '4px 0 0',
      paddingLeft : 20
    }
  },
  // the name field matches the vicinity search card's labelled field ...
  nameGroup : {
    gap : 5
  },
  label : {
    padding : '0 8px'
  }
}));

/**
 * Renders the current theme's saved map view name form: the map views
 * description, the parameters a saved view keeps, the "Name the custom
 * view" field (with its duplicate-name error) and Cancel / Done actions.
 * Done is disabled until a new, unused name is entered.
 *
 * ```typescript
 *
 *   import {SavedMapViewsCurrentInput} from
 *     '@ion/dls/src/maps/components/views/SavedMapViewsCurrentInput';
 *   ...
 *
 *   <SavedMapViewsCurrentInput savedMapViews={savedMapViews}
 *     mapViewToEdit={mapViewToEdit}
 *     onCancel={onCancel}
 *     onSave={onSave} />
 *
 * ```
 *
 * @param   savedMapViews The saved map views, for the duplicate name check.
 * @param   mapViewToEdit The saved map view being renamed, if any.
 * @param   onCancel      Invoked by the Cancel button.
 * @param   onSave        Invoked with `{inputValue}` by the Done button.
 * @returns               The saved map view name form.
 */
export const SavedMapViewsCurrentInput = ({
  savedMapViews,
  mapViewToEdit,
  onCancel,
  onSave
}: SavedMapViewsCurrentInputProps) => {
  const cardClasses = useGeofenceCardStyles();
  const classes     = useStyles();
  const {inputValue, saveDisabled, onNameChange, onSubmit} = useSavedMapViewName({
    savedMapViews,
    mapViewToEdit,
    onSave
  });

  const parameters: string[] = useMemo(() => (
    translate('maps:mapViews.descriptionList').split(',')
  ), []);

  const nameLabel = mapViewToEdit
    ? translate('maps:mapViews.inputPlaceholderEdit')
    : translate('maps:mapViews.inputPlaceholder');

  return (
    <div className={classnames(cardClasses.body, classes.body)}>
      <p className={cardClasses.description}>
        {translate('maps:mapViews.currentCard.description')}
      </p>
      <div className={classes.parameters}>
        {translate('maps:mapViews.currentCard.parametersIntro')}
        <ul>
          {parameters.map(function(parameter) {
            return (
              <li key={parameter}>
                {parameter.trim()}
              </li>
            );
          })}
        </ul>
      </div>
      <div className={classnames(cardClasses.searchGroup, classes.nameGroup)}>
        <span className={classnames(cardClasses.label, classes.label)}>
          {nameLabel}
        </span>
        <TextField id='save-view-search'
          type='search'
          fullWidth
          autoFocus
          defaultValue={mapViewToEdit?.id}
          placeholder={nameLabel}
          hasError={saveDisabled}
          errorText={translate('maps:mapViews.duplicateName')}
          onChange={onNameChange} />
      </div>
      <div className={cardClasses.actions}>
        <TertiaryButton onClick={onCancel}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>
        <PrimaryButton type='submit'
          disabled={saveDisabled || !inputValue.length}
          onClick={onSubmit}>
          {translate('common:editors.shared.done')}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default SavedMapViewsCurrentInput;
